import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../lib/api';
import { ConfirmButton } from '../StaffAppItemDetail';
import { Spinner } from './ToolHeader';

const sign = (n) => (n > 0 ? `+${n}` : `${n}`);

/** Admin review of a submitted count: variances vs system stock, approve or send back. */
export default function StocktakeApprovalPanel({ sessionId, onChanged }) {
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get(`/stocktake/sessions/${sessionId}/variance`).then((r) => setReport(r.data)).catch(() => toast.error('Could not load variances')); }, [sessionId]);
  if (!report) return <Spinner />;
  const s = report.summary;
  const diffs = report.variances.filter((v) => v.variance !== 0);

  const act = async (path, msg) => {
    setBusy(true);
    try {
      const { data } = await api.post(path);
      toast.success(data.message || msg);
      if (path.endsWith('/approve')) navigate('/staff-app/tools/stocktake'); else onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || 'Action failed'); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="staff-app-stocktake-approval">
      <div className="grid grid-cols-4 gap-1.5 text-center">
        {[['Counted', s.counted], ['Match', s.matches], ['Over', s.over], ['Under', s.under]].map(([k, v]) => (
          <div key={k} className="rounded-lg bg-muted py-2"><p className="text-lg font-bold tabular-nums">{v}</p><p className="text-[10px] text-muted-foreground">{k}</p></div>
        ))}
      </div>
      <div className="rounded-xl border border-border bg-card p-3 flex justify-between text-sm" data-testid="staff-app-stocktake-cost-variance">
        <span>Cost impact</span>
        <b className={s.total_cost_variance < 0 ? 'text-destructive' : 'text-emerald-600'}>{sign(s.total_cost_variance.toFixed(2))}</b>
      </div>
      {s.not_counted > 0 && <p className="text-xs text-amber-600" data-testid="staff-app-stocktake-not-counted">{s.not_counted} product(s) not counted — their stock stays unchanged.</p>}
      <div className="divide-y divide-border rounded-xl border border-border bg-card" data-testid="staff-app-stocktake-variances">
        {diffs.length === 0 && <p className="px-3.5 py-5 text-center text-sm text-muted-foreground">Every counted product matches the system.</p>}
        {diffs.map((v) => (
          <div key={v.product_id} className="flex items-center justify-between gap-2 px-3.5 py-2.5" data-testid={`staff-app-variance-${v.product_id}`}>
            <span className="min-w-0">
              <span className="block text-sm font-medium truncate">{v.product_name}</span>
              <span className="block text-[11px] text-muted-foreground">System {v.system_quantity} → counted {v.counted_quantity}</span>
            </span>
            <span className={`text-sm font-bold tabular-nums ${v.variance < 0 ? 'text-destructive' : 'text-sky-600'}`}>{sign(v.variance)}</span>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <ConfirmButton label="Send back" variant="outline" loading={busy} testId="staff-app-stocktake-return" onConfirm={() => act(`/staff-app/stocktake/${sessionId}/return`, 'Sent back for recount')}
          title="Send back for recount?" description="The count reopens so staff can correct it. Nothing changes in stock." />
        <ConfirmButton label="Approve" loading={busy} testId="staff-app-stocktake-approve" onConfirm={() => act(`/stocktake/sessions/${sessionId}/approve`, 'Approved')}
          title="Approve and update stock?" description={`Stock for ${s.counted} counted product(s) will be set to the counted quantity (${diffs.length} change${diffs.length === 1 ? '' : 's'}). This can't be undone from the app.`} />
      </div>
    </div>
  );
}
